import { Modal } from "antd";
import i18n from "../../../i18n";

type ItemMasterCsvErrorDetail = {
  rowNumber?: number;
  code?: string;
  message?: string;
};

export type ItemMasterCsvErrorPayload = {
  message?: string;
  rowNumber?: number;
  errorCode?: string;
  errors?: ItemMasterCsvErrorDetail[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const toStringValue = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const toNumberValue = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const ERROR_CODE_TRANSLATION_PREFIX = "errorCodes";

const formatRowMessage = (rowNumber?: number, message?: string): string | undefined => {
  if (!message) {
    return undefined;
  }

  return rowNumber !== undefined ? `${rowNumber}${message}` : message;
};

export const parseItemMasterCsvErrorPayload = async (
  response: Response
): Promise<ItemMasterCsvErrorPayload | undefined> => {
  try {
    const data = await response.clone().json();
    if (!isRecord(data)) return undefined;

    const message = toStringValue(data.message);
    const rowNumber = toNumberValue(data.rowNumber);
    const errorCode = toStringValue(data.error_code);
    const errors = Array.isArray(data.errors)
      ? data.errors
        .filter(isRecord)
        .map((error) => ({
          rowNumber: toNumberValue(error.rowNumber),
          code: toStringValue(error.code),
          message: toStringValue(error.message),
        }))
      : undefined;

    if (!message && rowNumber === undefined && !errorCode && (!errors || errors.length === 0)) {
      return undefined;
    }

    return { message, rowNumber, errorCode, errors };
  } catch (_error) {
    console.warn("Failed to parse item master CSV response");
    return undefined;
  }
};

export const showItemMasterCsvUploadErrorModal = (
  fileName: string,
  payload?: ItemMasterCsvErrorPayload,
  fallbackMessage?: string
) => {
  const errorCodeTranslationKey = payload?.errorCode ? `${ERROR_CODE_TRANSLATION_PREFIX}.${payload.errorCode}` : null;
  const hasTranslatedErrorCode = Boolean(errorCodeTranslationKey && i18n.exists(errorCodeTranslationKey));
  const shouldShowApiMessages = !i18n.language.startsWith("en") && !hasTranslatedErrorCode;
  const detailMessages = payload?.errors
    ?.map((error) => formatRowMessage(error.rowNumber, error.message))
    .filter((message): message is string => Boolean(message));

  const contentLines = hasTranslatedErrorCode
    ? [i18n.t(errorCodeTranslationKey as string)]
    : shouldShowApiMessages
      ? [
        payload?.message,
        ...(detailMessages ?? []),
      ].filter((line, index, array): line is string => Boolean(line) && array.indexOf(line) === index)
      : [];

  Modal.error({
    centered: true,
    title: i18n.t("itemMaster.csvRegistrationError"),
    content: (
      <div className="whitespace-pre-wrap">
        {contentLines.length > 0
          ? contentLines.map((line, index) => <div key={`${line}-${index}`}>{line}</div>)
          : <div>{fallbackMessage ?? i18n.t("itemMaster.uploadFailed", { fileName })}</div>}
      </div>
    ),
  });
};
